import { useTranslation } from 'react-i18next'
import { LanguageSwitcher } from '../components/LanguageSwitcher'

/** Branded Keycloak login gate (A1/A5) — rendered when no active session. */
export default function LoginGate({ onLogin }: { onLogin: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0b1120] p-6">
      {/* ambient background */}
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        <div className="absolute -left-24 -top-24 h-96 w-96 rounded-full bg-brand-600/20 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-brand-900/40 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #94a3b8 1px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />
      </div>

      <div className="absolute right-6 top-6 z-10">
        <LanguageSwitcher dark />
      </div>

      <div className="relative z-10 w-full max-w-md rounded-2xl border border-white/10 bg-white/5 p-8 shadow-2xl backdrop-blur-xl">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-600 to-brand-900 shadow-lg shadow-brand-600/30">
            <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6 text-white" aria-hidden>
              <path d="M12 2L4 5v6c0 5 3.4 9.4 8 11 4.6-1.6 8-6 8-11V5l-8-3z" stroke="currentColor" strokeWidth="1.6" />
              <path d="M8.5 12l2.5 2.5L16 9.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">{t('app.name')}</h1>
            <p className="text-xs text-slate-400">{t('app.tagline')}</p>
          </div>
        </div>

        <ul className="mb-8 space-y-2.5 text-sm text-slate-300">
          {[
            'Verified against official registries',
            'Every fact traceable to its source',
            'Daily signals from 5+ countries',
          ].map((line) => (
            <li key={line} className="flex items-center gap-2">
              <svg viewBox="0 0 20 20" className="h-4 w-4 text-emerald-400" fill="currentColor" aria-hidden>
                <path d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L8 13.6l7.3-7.3a1 1 0 011.4 0z" />
              </svg>
              {line}
            </li>
          ))}
        </ul>

        <button
          id="vb-login"
          onClick={onLogin}
          className="w-full rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 py-3 font-semibold text-white shadow-lg shadow-brand-600/30 transition-all hover:shadow-brand-600/50 hover:brightness-110"
        >
          {t('login.signIn', 'Sign in')}
        </button>
        <p className="mt-4 text-center text-xs text-slate-500">
          {t('login.secured', 'Secured with Keycloak · MFA-ready')}
        </p>
      </div>
    </div>
  )
}
